import React, { useState, useMemo, useRef, useEffect } from 'react';
import './FeedingCalculator.css';
import BCSChartModal from './BCSChartModal';
import { DOG_BREEDS, searchBreeds, BreedData, getBreedSize, getBreedCategories, BreedCategory, searchBreedsInCategory } from '../utils/breedsData';
import { getDERMultiplier, calculateIdealWeight, calculateRER, mapSizeCategory } from '../utils/derTable';

// Helper function to determine adult age threshold based on breed size
const getAdultAgeThreshold = (sizeCategory: string): { months: number; label: string } => {
  switch (sizeCategory) {
    case 'Toy-Small':
    case 'Small':
      return { months: 10, label: 'Adult (10+ months)' };
    case 'Small-Medium':
    case 'Medium':
    case 'Medium (25-30)':
    case 'Medium (30-40)':
    case 'Medium (40-50)':
      return { months: 12, label: 'Adult (12+ months)' };
    case 'Medium-Large':
    case 'Large':
      return { months: 15, label: 'Adult (15+ months)' };
    case 'Large-Giant':
      return { months: 18, label: 'Adult (18+ months)' };
    default:
      return { months: 15, label: 'Adult (15+ months)' }; // Default fallback
  }
};

// Helper function to check if current age qualifies as adult based on breed size
const isAdultAge = (age: string, sizeCategory: string): boolean => {
  const threshold = getAdultAgeThreshold(sizeCategory);
  
  switch (age) {
    case '1-3months':
    case '4-6months':
    case '7-9months':
      return false;
    case '10-12months':
      return threshold.months <= 10;
    case '13-15months':
      return threshold.months <= 13;
    case '16-18months':
      return threshold.months <= 16;
    case '19-24months':
      return threshold.months <= 19;
    case 'adult':
      return true;
    default:
      return false;
  }
};

interface FeedingResult {
  calorieRange: {
    min: number;
    max: number;
    target: number;
  };
  profile: {
    weight: number;
    weightUnit: string;
    age: string;
    activity: string;
    breed: string;
    breedSizeCategory: string;
    bodyCondition: number;
    neuteredStatus: string;
  };
  macronutrients: {
    protein: number;
    fat: number;
    carbs: number;
  };
  mealFrequency: {
    meals: number;
    description: string;
  };
  mealPlans: {
    type: string;
    title: string;
    items: {
      food: string;
      amount: string;
      calories: number;
    }[];
    totalCalories: number;
  }[];
  specialNotes: string[];
}

interface FeedingCalculatorProps {
  onClose: () => void;
}

const FeedingCalculator: React.FC<FeedingCalculatorProps> = ({ onClose }) => {
  // Form state
  const [weight, setWeight] = useState<string>('');
  const [weightUnit, setWeightUnit] = useState<'kg' | 'lb'>('kg');
  const [breed, setBreed] = useState<string>('');
  const [selectedBreedData, setSelectedBreedData] = useState<BreedData | null>(null);
  const [breedSearchTerm, setBreedSearchTerm] = useState<string>('');
  const [showBreedDropdown, setShowBreedDropdown] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<BreedCategory | null>(null);
  const [showCategories, setShowCategories] = useState<boolean>(true);
  const [globalSearchTerm, setGlobalSearchTerm] = useState<string>('');
  const [showGlobalSearchResults, setShowGlobalSearchResults] = useState<boolean>(false);
  const [age, setAge] = useState<'1-3months' | '4-6months' | '7-9months' | '10-12months' | '13-15months' | '16-18months' | '19-24months' | 'adult'>('adult');
  const [activity, setActivity] = useState<'low' | 'normal' | 'high'>('normal');
  const [neuteredStatus, setNeuteredStatus] = useState<'neutered' | 'intact'>('neutered');
  const [bcs, setBcs] = useState<number>(5);
  const [showBCSChart, setShowBCSChart] = useState<boolean>(false);
  const [result, setResult] = useState<FeedingResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // Ref for breed dropdown
  const breedDropdownRef = useRef<HTMLDivElement>(null);
  const globalSearchRef = useRef<HTMLDivElement>(null);

  // Get breed categories
  const breedCategories = useMemo(() => {
    return getBreedCategories();
  }, []);

  // Filtered breeds for dropdown (when category is selected)
  const filteredBreeds = useMemo(() => {
    if (selectedCategory) {
      return searchBreedsInCategory(breedSearchTerm, selectedCategory);
    }
    return [];
  }, [breedSearchTerm, selectedCategory]);

  // Global search results (across all breeds)
  const globalSearchResults = useMemo(() => {
    if (globalSearchTerm.length >= 2) {
      return searchBreeds(globalSearchTerm).slice(0, 8); // Limit to 8 results for cleaner UX
    }
    return [];
  }, [globalSearchTerm]);

  // Handle click outside breed dropdown and global search
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (breedDropdownRef.current && !breedDropdownRef.current.contains(event.target as Node)) {
        setShowBreedDropdown(false);
      }
      if (globalSearchRef.current && !globalSearchRef.current.contains(event.target as Node)) {
        setShowGlobalSearchResults(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowBreedDropdown(false);
        setShowGlobalSearchResults(false);
      }
    };

    if (showBreedDropdown || showGlobalSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showBreedDropdown, showGlobalSearchResults]);

  const handleBreedSelect = (selectedBreed: BreedData) => {
    setBreed(selectedBreed.name);
    setSelectedBreedData(selectedBreed);
    setBreedSearchTerm(selectedBreed.name);
    setShowBreedDropdown(false);
    setShowCategories(false);
    setGlobalSearchTerm('');
    setShowGlobalSearchResults(false);
  };

  const handleGlobalSearchSelect = (selectedBreed: BreedData) => {
    setBreed(selectedBreed.name);
    setSelectedBreedData(selectedBreed);
    setBreedSearchTerm(selectedBreed.name);
    setGlobalSearchTerm('');
    setShowGlobalSearchResults(false);
    setShowCategories(false);
    setShowBreedDropdown(false);
  };

  const handleCategorySelect = (category: BreedCategory) => {
    setSelectedCategory(category);
    setShowCategories(false);
    setShowBreedDropdown(true);
    setBreedSearchTerm('');
  };

  const handleBackToCategories = () => {
    setBreed('');
    setSelectedBreedData(null);
    setSelectedCategory(null);
    setShowCategories(true);
    setShowBreedDropdown(false);
    setBreedSearchTerm('');
    setGlobalSearchTerm('');
    setShowGlobalSearchResults(false);
  };

  // Generate age options based on selected breed size
  // Removes overlapping options based on when breed reaches adulthood
  const getAgeOptions = () => {
    if (!selectedBreedData) {
      // Default options when no breed is selected
      return [
      { value: '1-3months', label: '1-3 Months' },
      { value: '4-6months', label: '4-6 Months' },
      { value: '7-9months', label: '7-9 Months' },
      { value: '10-12months', label: '10-12 Months' },
      { value: '13-15months', label: '13-15 Months' },
        { value: 'adult', label: 'Adult (15+ months)' }
      ];
    }

    const adultThreshold = getAdultAgeThreshold(selectedBreedData.sizeCategory);
    const options: { value: string; label: string }[] = [];
    
    // Always include early puppy stages
    options.push({ value: '1-3months', label: '1-3 Months' });
    options.push({ value: '4-6months', label: '4-6 Months' });
    options.push({ value: '7-9months', label: '7-9 Months' });
    
    // Only add age ranges that are BEFORE the adult threshold
    // This prevents overlap (e.g., showing both "10-12 Months" and "Adult (10+ months)")
    if (adultThreshold.months > 10) {
      options.push({ value: '10-12months', label: '10-12 Months' });
    }
    if (adultThreshold.months > 13) {
      options.push({ value: '13-15months', label: '13-15 Months' });
    }
    if (adultThreshold.months > 15) {
      options.push({ value: '16-18months', label: '16-18 Months' });
    }
    if (adultThreshold.months > 18) {
      options.push({ value: '19-24months', label: '19-24 Months' });
    }
    
    // Add the breed-specific adult option
    options.push({ value: 'adult', label: adultThreshold.label });
    
    return options;
  };

  // Check if activity level should be shown based on age and breed size
  const shouldShowActivityLevel = () => {
    if (!selectedBreedData) {
      return age === '13-15months' || age === 'adult';
    }
    
    return isAdultAge(age, selectedBreedData.sizeCategory) || age === '13-15months' || age === '16-18months' || age === '19-24months' || age === 'adult';
  };

  const convertToKg = (weightValue: number, unit: 'kg' | 'lb'): number => {
    return unit === 'lb' ? weightValue * 0.453592 : weightValue;
  };

  // Helper function to convert grams to cups (approximate)
  // Dry kibble: ~120g per cup, Fresh food: ~240g per cup
  const gramsToCups = (grams: number, foodType: 'dry' | 'fresh'): string => {
    const gramsPerCup = foodType === 'dry' ? 120 : 240;
    const cups = grams / gramsPerCup;
    
    if (cups < 0.25) return `${grams}g (⅛ cup)`;
    if (cups < 0.375) return `${grams}g (¼ cup)`;
    if (cups < 0.625) return `${grams}g (½ cup)`;
    if (cups < 0.875) return `${grams}g (¾ cup)`;
    if (cups < 1.25) return `${grams}g (1 cup)`;
    
    const wholeCups = Math.floor(cups);
    const remainder = cups - wholeCups;
    
    if (remainder < 0.125) return `${grams}g (${wholeCups} cups)`;
    if (remainder < 0.375) return `${grams}g (${wholeCups}¼ cups)`;
    if (remainder < 0.625) return `${grams}g (${wholeCups}½ cups)`;
    if (remainder < 0.875) return `${grams}g (${wholeCups}¾ cups)`;
    return `${grams}g (${wholeCups + 1} cups)`;
  };

  const getBCSDescription = (score: number): string => {
    const descriptions: { [key: number]: string } = {
      1: 'Emaciated',
      2: 'Very Thin',
      3: 'Thin',
      4: 'Moderately Thin',
      5: 'Ideal',
      6: 'Moderately Above Ideal',
      7: 'Overweight',
      8: 'Obesity',
      9: 'Severe Obesity'
    };
    return descriptions[score] || 'Unknown';
  };

  const calculateCalories = async () => {
    // Validation
    if (!weight || parseFloat(weight) <= 0) {
      alert('Please enter a valid weight');
      return;
    }

    if (!breed || breed.trim() === '') {
      alert('Please select a breed from the dropdown');
      return;
    }

    setIsCalculating(true);
    
    // Simulate calculation delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    const weightValue = parseFloat(weight);
    const weightKg = convertToKg(weightValue, weightUnit);
    
    // Step 1: Calculate Ideal Body Weight if BCS is not 5
    // Veterinary standard: Each BCS point above/below 5 represents ~10% deviation from ideal weight
    // For overweight dogs (BCS 6-9): Ideal Weight = Current Weight / (1 + (BCS-5) * 0.10)
    // For underweight dogs (BCS 1-4): Ideal Weight = Current Weight / (1 - (5-BCS) * 0.10)
    const idealWeightKg = calculateIdealWeight(weightKg, bcs);
    
    // Step 2: Calculate Resting Energy Requirement (RER) using ideal weight
    // RER = 70 x (Ideal Body Weight in kg)^0.75
    const rer = calculateRER(idealWeightKg);
    
    // Step 3: Get breed size category
    // Use selectedBreedData.sizeCategory first (covers designer/mixed breeds not in DOG_BREEDS),
    // then fall back to the DOG_BREEDS lookup
    const breedSizeCategory = selectedBreedData?.sizeCategory || getBreedSize(breed);
    if (!breedSizeCategory || breedSizeCategory === 'Variable') {
      // For "Variable" size (Mixed Breed), estimate size from weight
      const estimatedSize = weightKg < 10 ? 'Small' : weightKg < 25 ? 'Medium' : weightKg < 45 ? 'Large' : 'Large-Giant';
      const derSizeKeyFallback = mapSizeCategory(estimatedSize, weightKg);
      const derMultiplierFallback = getDERMultiplier(neuteredStatus, derSizeKeyFallback, age, activity, bcs);
      const derFallback = rer * derMultiplierFallback;
      const baseCaloriesFallback = Math.round(derFallback);
      const calorieRangeFallback = {
        min: Math.round(baseCaloriesFallback * 0.92),
        max: Math.round(baseCaloriesFallback * 1.08),
        target: baseCaloriesFallback
      };
      let breedSizeFallback: string;
      if (weightKg < 10) breedSizeFallback = 'small';
      else if (weightKg < 25) breedSizeFallback = 'medium';
      else if (weightKg < 45) breedSizeFallback = 'large';
      else breedSizeFallback = 'giant';
      const macronutrientsFallback = calculateMacronutrients(age, breedSizeFallback, activity);
      const mealFrequencyFallback = calculateMealFrequency(age, breedSizeFallback);
      const weightLbsFallback = weightUnit === 'lb' ? weightValue : weightValue * 2.20462;
      const mealPlansFallback = generateMealPlans(baseCaloriesFallback, age, breedSizeFallback, weightLbsFallback, activity);
      const specialNotesFallback = generateSpecialNotes(age, breedSizeFallback, bcs, activity, neuteredStatus, weightKg, idealWeightKg);
      setResult({
        calorieRange: calorieRangeFallback,
        profile: { weight: weightValue, weightUnit, age, activity, breed, breedSizeCategory: estimatedSize, bodyCondition: bcs, neuteredStatus },
        macronutrients: macronutrientsFallback,
        mealFrequency: mealFrequencyFallback,
        mealPlans: mealPlansFallback,
        specialNotes: specialNotesFallback
      });
      setIsCalculating(false);
      return;
    }
    
    // Map the size category to DER table format
    const derSizeKey = mapSizeCategory(breedSizeCategory, weightKg);
    
    // Step 4: Get DER multiplier from table
    const derMultiplier = getDERMultiplier(neuteredStatus, derSizeKey, age, activity, bcs);
    
    // Step 5: Calculate Daily Energy Requirement
    // DER = RER x multiplier
    const der = rer * derMultiplier;
    const baseCalories = Math.round(der);
    
    // Create calorie range (±8%)
    const calorieRange = {
      min: Math.round(baseCalories * 0.92),
      max: Math.round(baseCalories * 1.08),
      target: baseCalories
    };
    
    // Determine breed size from weight for macronutrient calculation
    let breedSize: string;
    if (weightKg < 10) breedSize = 'small';
    else if (weightKg < 25) breedSize = 'medium';
    else if (weightKg < 45) breedSize = 'large';
    else breedSize = 'giant';

    // Calculate macronutrients
    const macronutrients = calculateMacronutrients(age, breedSize, activity);
    
    // Determine meal frequency
    const mealFrequency = calculateMealFrequency(age, breedSize);
    
    // Generate meal plans (pass weight in lbs for portion calculations)
    const weightLbs = weightUnit === 'lb' ? weightValue : weightValue * 2.20462;
    const mealPlans = generateMealPlans(baseCalories, age, breedSize, weightLbs, activity);
    
    // Generate personalized notes
    const specialNotes = generateSpecialNotes(age, breedSize, bcs, activity, neuteredStatus, weightKg, idealWeightKg);

    setResult({
      calorieRange,
      profile: {
        weight: weightValue,
        weightUnit,
        age,
        activity,
        breed: breed,
        breedSizeCategory: breedSizeCategory || 'Medium',
        bodyCondition: bcs,
        neuteredStatus
      },
      macronutrients,
      mealFrequency,
      mealPlans,
      specialNotes
    });

    setIsCalculating(false);
  };

  const calculateMacronutrients = (ageGroup: string, breedSize: string, activityLevel: string) => {
    let protein = 25;
    let fat = 12;
    
    // Age-specific adjustments
    if (ageGroup.includes('months')) {
      protein = 28;
      fat = 15;
    } else { // adult
      protein = 24;
      fat = 10;
    }
    
    // Activity adjustments
    if (activityLevel === 'high') {
      protein += 3;
      fat += 2;
    } else if (activityLevel === 'low') {
      protein -= 1;
      fat -= 1;
    }
    
    // Breed size considerations
    if (breedSize === 'large' || breedSize === 'giant') {
      protein += 2;
    }
    
    protein = Math.max(protein, 18);
    fat = Math.max(fat, 8);
    
    const carbs = 100 - protein - fat;
    
    return { protein, fat, carbs };
  };

  const calculateMealFrequency = (ageGroup: string, breedSize: string) => {
    if (ageGroup === '1-3months') {
      return {
        meals: 4,
        description: '4 meals/day (young puppies need frequent feeding)'
      };
    } else if (ageGroup === '4-6months' || ageGroup === '7-9months') {
      return {
        meals: 3,
        description: '3 meals/day (growing puppies)'
      };
    } else if (ageGroup === '10-12months' || ageGroup === '13-15months') {
      return {
        meals: breedSize === 'small' ? 2 : 3,
        description: `${breedSize === 'small' ? '2' : '3'} meals/day (transitioning to adult)`
      };
    } else { // adult
      return {
        meals: breedSize === 'giant' ? 3 : 2,
        description: `${breedSize === 'giant' ? '3' : '2'} meals/day (${breedSize === 'giant' ? 'prevents bloat in giant breeds' : 'optimal for adults'})`
      };
    }
  };

  const generateMealPlans = (calories: number, ageGroup: string, breedSize: string, dogWeightLbs: number, activityLevel: 'low' | 'normal' | 'high') => {
    const plans = [];
    
    // Helper function to get InstaBites portion from your feeding guide
    const getInstaBitesPortion = (weightLbs: number, activity: string) => {
      const feedingGuide: Record<number, { low_activity: string; high_activity: string }> = {
        5: { low_activity: "1/4 cup", high_activity: "1/2 cup" },
        10: { low_activity: "1/2 cup", high_activity: "3/4 cup" },
        20: { low_activity: "1 cup", high_activity: "1 1/4 cups" },
        30: { low_activity: "1 1/4 cups", high_activity: "1 3/4 cups" },
        40: { low_activity: "1 1/2 cups", high_activity: "2 1/4 cups" },
        60: { low_activity: "2 1/4 cups", high_activity: "3 cups" },
        80: { low_activity: "2 3/4 cups", high_activity: "3 3/4 cups" },
        100: { low_activity: "3 1/4 cups", high_activity: "4 1/2 cups" }
      };
      
      // Find closest weight in feeding guide
      const weights = Object.keys(feedingGuide).map(Number).sort((a, b) => a - b);
      let closestWeight = weights.find(w => w >= weightLbs) || weights[weights.length - 1];
      
      // If over 100 lbs, calculate additional
      if (weightLbs > 100) {
        const extraWeight = weightLbs - 100;
        const extraCups = Math.ceil(extraWeight / 10) * 0.25; // 1/4 cup per 10 lbs
        const baseCups = activity === 'high' ? 4.5 : 3.25;
        return `${(baseCups + extraCups).toFixed(2)} cups`;
      }
      
      const activityKey = activity === 'high' ? 'high_activity' : 'low_activity';
      return feedingGuide[closestWeight][activityKey];
    };
    
    // Helper function to calculate grams based on calories and caloric density
    // Fresh dog food: approximately 1.0-1.2 kcal per gram
    // Dry kibble: approximately 3.5-4.0 kcal per gram
    const calculateGrams = (targetCalories: number, foodType: 'fresh' | 'dry'): number => {
      const kcalPerGram = foodType === 'fresh' ? 1.1 : 3.5;
      return Math.round(targetCalories / kcalPerGram);
    };
    
    // Helper function to format grams with cup equivalent
    const formatPortionWithCups = (grams: number, foodType: 'fresh' | 'dry'): string => {
      // Fresh food: ~240g per cup, Dry food: ~120g per cup
      const gramsPerCup = foodType === 'fresh' ? 240 : 120;
      const cups = grams / gramsPerCup;
      
      let cupText = '';
      if (cups < 0.25) {
        cupText = '⅛ cup';
      } else if (cups < 0.375) {
        cupText = '¼ cup';
      } else if (cups < 0.625) {
        cupText = '½ cup';
      } else if (cups < 0.875) {
        cupText = '¾ cup';
      } else if (cups < 1.25) {
        cupText = '1 cup';
      } else if (cups < 1.625) {
        cupText = '1½ cups';
      } else if (cups < 2.25) {
        cupText = '2 cups';
      } else if (cups < 2.75) {
        cupText = '2½ cups';
      } else if (cups < 3.25) {
        cupText = '3 cups';
      } else if (cups < 3.75) {
        cupText = '3½ cups';
      } else {
        cupText = `${Math.round(cups * 2) / 2} cups`;
      }
      
      return `${grams}g (${cupText})`;
    };
    
    // Calculate portions based on caloric needs
    const freshFoodGrams = calculateGrams(calories, 'fresh');
    const dryFoodGrams = calculateGrams(calories, 'dry');
    
    // Plan 1: PawCo LuxBites (Premium - Always First)
    // Fresh food with approximately 1.1 kcal/g
    const luxBitesPortion = formatPortionWithCups(freshFoodGrams, 'fresh');
    
    plans.push({
      type: 'pawco-luxbites',
      title: '🏆 Pawco LuxBites Fresh (Premium)',
      items: [
        {
          food: 'Pawco LuxBites Fresh - Premium fermented protein with postbiotics for optimal gut health, immunity, and zest for life',
          amount: luxBitesPortion,
          calories: calories
        }
      ],
      totalCalories: calories
    });
    
    // Plan 2: PawCo InstaBites (Shelf Stable) - Using calorie-based portion
    // InstaBites is a dry food - approximately 3.5 kcal/g
    const instaBitesPortion = formatPortionWithCups(dryFoodGrams, 'dry');
    
    plans.push({
      type: 'pawco-instabites',
      title: '🥄 Pawco InstaBites (Shelf Stable)',
      items: [
        {
          food: 'Pawco InstaBites - Complete vegan nutrition with oat flour, pea protein, and marine microalgae oil',
          amount: `${instaBitesPortion} dry (add 3 tbsp warm water per 1/4 cup)`,
          calories: calories
        }
      ],
      totalCalories: calories
    });
    
    // Plan 3: PawCo GreenBites Fresh
    // Fresh food with approximately 1.1 kcal/g
    const greenBitesPortion = formatPortionWithCups(freshFoodGrams, 'fresh');
    
    // Rotate between GreenBites flavors from your product data
    const flavors = ['Green mix & Carrots', 'Peanut butter & Apple', 'Blueberries & Veggies', 'Pumpkin & Cranberries'];
    const selectedFlavor = flavors[Math.floor(Math.random() * flavors.length)];
    
    plans.push({
      type: 'pawco-greenbites',
      title: '🌱 Pawco GreenBites Fresh',
      items: [
        {
          food: `Pawco GreenBites ${selectedFlavor} - Fresh vegan meals with textured soy protein, probiotics, and marine microalgae`,
          amount: greenBitesPortion,
          calories: calories
        }
      ],
      totalCalories: calories
    });
    
    // Plan 4: PawCo Mixed Plan with Toppers
    const mixedKibbleCalories = Math.round(calories * 0.85);
    const topperCalories = calories - mixedKibbleCalories;
    const mixedDryGrams = calculateGrams(mixedKibbleCalories, 'dry');
    const mixedInstaBitesPortion = formatPortionWithCups(mixedDryGrams, 'dry');
    
    // Select appropriate topper based on dog needs
    let topperType = 'Original';
    if (ageGroup.includes('months')) {
      topperType = 'Gut Health'; // Puppies need digestive support
    } else if (breedSize === 'large' || breedSize === 'giant') {
      topperType = 'Hip & Joint'; // Large breeds need joint support
    }
    
    plans.push({
      type: 'pawco-mixed',
      title: '✨ Pawco Complete Plan (InstaBites + MagicTopper)',
      items: [
        {
          food: 'Pawco InstaBites - Base nutrition',
          amount: mixedInstaBitesPortion,
          calories: mixedKibbleCalories
        },
        {
          food: `Pawco MagicTopper ${topperType} - Nutritional yeast, vegetables, and targeted supplements`,
          amount: `${dogWeightLbs >= 20 ? '1 tbsp' : '½ tbsp'}/day (per feeding guide)`,
          calories: topperCalories
        }
      ],
      totalCalories: calories
    });
    
    // Plan 5: PawCo Premium Plan with Magic Bars
    const magicBarCalories = Math.round(calories * 0.1); // 10% of daily calories as treats
    const baseCaloriesForBars = calories - magicBarCalories;
    const premiumDryGrams = calculateGrams(baseCaloriesForBars, 'dry');
    const premiumInstaBitesPortion = formatPortionWithCups(premiumDryGrams, 'dry');
    
    // Calculate Magic Bar serving: 1/4 bar per 20 lbs body weight
    const magicBarServing = Math.max(0.25, (dogWeightLbs / 20) * 0.25); // Minimum 1/4 bar
    // Fixed rounding thresholds: round to nearest quarter bar
    const magicBarText = magicBarServing < 0.375 ? '¼ bar' : 
                        magicBarServing < 0.625 ? '½ bar' : 
                        magicBarServing < 0.875 ? '¾ bar' : 
                        magicBarServing < 1.125 ? '1 bar' :
                        `${Math.round(magicBarServing * 4) / 4} bars`;
    
    plans.push({
      type: 'pawco-premium',
      title: '🏆 Pawco Premium Plan (InstaBites + Magic Bar)',
      items: [
        {
          food: 'Pawco InstaBites - Base nutrition',
          amount: premiumInstaBitesPortion,
          calories: baseCaloriesForBars
        },
        {
          food: 'Pawco Magic Bar (Original or Gut Health) - Premium treat with calorie-dense nutrition',
          amount: `${magicBarText} (${magicBarServing.toFixed(2)} × ¼ bar per 20 lbs body weight)`,
          calories: magicBarCalories
        }
      ],
      totalCalories: calories
    });
    
    return plans;
  };

  const generateSpecialNotes = (
    ageGroup: string, 
    breedSize: string, 
    bodyCondition: number,
    activityLevel: string, 
    neutered: string,
    currentWeight: number,
    idealWeight: number
  ) => {
    const notes = [];
    
    // Ideal weight note if BCS is not 5
    if (bodyCondition !== 5) {
      const weightDiff = Math.abs(currentWeight - idealWeight);
      notes.push(`TARGET WEIGHT: ${idealWeight.toFixed(1)} kg (current: ${currentWeight.toFixed(1)} kg, ${weightDiff > 0 ? (bodyCondition < 5 ? 'gain' : 'loss') : ''} needed: ${weightDiff.toFixed(1)} kg)`);
    }
    
    // BCS-specific notes
    if (bodyCondition <= 2) {
      notes.push('🚨 CRITICAL: BCS ' + bodyCondition + ' - Immediate veterinary consultation required for severe underweight');
      notes.push('Consider more energy-dense foods and frequent small meals');
    } else if (bodyCondition === 3 || bodyCondition === 4) {
      notes.push('WARNING: BCS ' + bodyCondition + ' - Gradual weight gain recommended. Calories calculated for target weight');
      notes.push('Monitor weekly and increase exercise as weight normalizes');
    } else if (bodyCondition === 5) {
      notes.push('IDEAL: BCS 5 - Maintain current diet and exercise routine');
    } else if (bodyCondition === 6 || bodyCondition === 7) {
      notes.push('WARNING: BCS ' + bodyCondition + ' - Weight loss recommended. Calories calculated for target weight');
      notes.push('Increase exercise gradually and monitor progress weekly');
    } else if (bodyCondition >= 8) {
      notes.push('🚨 BCS ' + bodyCondition + ' - Veterinary-supervised weight loss program strongly recommended');
      notes.push('Obesity increases risk of diabetes, joint issues, and heart disease');
    }
    
    // Neutering status notes
    if (neutered === 'neutered') {
      notes.push('🔹 Neutered dogs have lower energy needs - DER adjusted accordingly');
    } else {
      notes.push('🔹 Intact dogs have higher energy requirements - DER adjusted for reproductive status');
    }
    
    // Age-specific notes
    if (ageGroup.includes('months')) {
      notes.push('PUPPY: Monitor growth weekly and adjust portions as needed for proper development');
      notes.push('Growing dogs require higher protein and fat for bone and muscle development');
    }
    
    // Activity notes
    if (activityLevel === 'high') {
      notes.push('💪 High activity → provide extra water and consider post-exercise meals');
    } else if (activityLevel === 'low') {
      notes.push('😴 Low activity → monitor weight closely to prevent obesity');
    }
    
    // Breed size notes
    if (breedSize === 'giant') {
      notes.push('🦴 Giant breed → 3 meals/day to prevent bloat, avoid exercise immediately after meals');
    }
    
    // Professional guidelines with PawCo branding
    notes.push('CALCULATIONS: Based on Merck Veterinary Manual RER formulas and DER multiplier tables');
    notes.push('🌱 PAWCO ADVANTAGE: All Pawco products provide complete vegan nutrition with B12, taurine, L-carnitine, and algae-based DHA for optimal canine health');
    notes.push('⭐ PAWCO QUALITY: As the leading vegan dog food company, Pawco ensures optimal nutrition with premium plant-based ingredients');
    notes.push('📏 MONITORING: Reassess BCS every 4 weeks and adjust portions ±10% as needed');
    notes.push('TRANSITION: 10-14 day gradual diet change recommended - Pawco products are designed for easy transition');
    
    return notes;
  };

  const getAgeLabel = (ageValue: string, sizeCategory?: string): string => {
    const labels: { [key: string]: string } = {
      '1-3months': '1-3 Months',
      '4-6months': '4-6 Months',
      '7-9months': '7-9 Months',
      '10-12months': '10-12 Months',
      '13-15months': '13-15 Months',
      '16-18months': '16-18 Months',
      '19-24months': '19-24 Months'
    };
    
    // For adult age, use breed-specific threshold
    if (ageValue === 'adult' && sizeCategory) {
      const threshold = getAdultAgeThreshold(sizeCategory);
      return threshold.label;
    }
    
    return labels[ageValue] || ageValue;
  };

  return (
    <>
    <div className="feeding-calculator-overlay">
      <div className="feeding-calculator-modal">
        <div className="feeding-calculator-header">
          <h2>Feeding Calculator</h2>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="feeding-calculator-content">
          {!result ? (
            <div className="input-section">
              {/* Main Form Layout: Left side for inputs, Right side for breed */}
              <div className="form-main-row">
                {/* Left Column: All form inputs */}
                <div className="form-left-column">
                  {/* Weight */}
                  <div className="form-group">
                    <label htmlFor="weight">Dog's Current Weight: *</label>
                    <div className="weight-input-group">
                      <input
                        type="number"
                        id="weight"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                        placeholder="Enter weight"
                        min="0.5"
                        step="0.1"
                        disabled={isCalculating}
                        className="weight-input"
                      />
                      <select
                        value={weightUnit}
                        onChange={(e) => setWeightUnit(e.target.value as 'kg' | 'lb')}
                        disabled={isCalculating}
                        className="weight-unit-select"
                      >
                        <option value="kg">kg</option>
                        <option value="lb">lb</option>
                      </select>
                    </div>
                    {weight && (
                      <span className="weight-conversion">
                        {weightUnit === 'kg' 
                          ? `≈ ${(parseFloat(weight) * 2.20462).toFixed(1)} lbs`
                          : `≈ ${(parseFloat(weight) * 0.453592).toFixed(1)} kg`
                        }
                      </span>
                    )}
                  </div>

                  {/* Age */}
                  <div className="form-group">
                    <label htmlFor="age">Age Group: *</label>
                    <select
                      id="age"
                      value={age}
                      onChange={(e) => setAge(e.target.value as any)}
                      disabled={isCalculating}
                    >
                      {getAgeOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Activity Level - show based on age and breed size */}
                  {shouldShowActivityLevel() && (
                    <div className="form-group">
                      <label htmlFor="activity">Activity Level: *</label>
                      <select
                        id="activity"
                        value={activity}
                        onChange={(e) => setActivity(e.target.value as 'low' | 'normal' | 'high')}
                        disabled={isCalculating}
                      >
                        <option value="low">Low (minimal exercise)</option>
                        <option value="normal">Normal (daily walks)</option>
                        <option value="high">High (very active/working dog)</option>
                      </select>
                    </div>
                  )}

                  {/* Neutered Status */}
                  <div className="form-group">
                    <label htmlFor="neuteredStatus">Neutered/Spayed Status: *</label>
                    <select
                      id="neuteredStatus"
                      value={neuteredStatus}
                      onChange={(e) => setNeuteredStatus(e.target.value as 'neutered' | 'intact')}
                      disabled={isCalculating}
                    >
                      <option value="neutered">Neutered/Spayed</option>
                      <option value="intact">Intact (Not neutered/spayed)</option>
                    </select>
                  </div>

                  {/* Body Condition Score */}
                  <div className="form-group">
                    <label htmlFor="bcs">
                      Body Condition Score (BCS): * 
                      <button
                        type="button"
                        className="bcs-chart-button"
                        onClick={() => setShowBCSChart(true)}
                        disabled={isCalculating}
                      >
                        <i className="fas fa-chart-bar"></i> View BCS Chart
                      </button>
                    </label>
                    <select
                      id="bcs"
                      value={bcs}
                      onChange={(e) => setBcs(parseInt(e.target.value))}
                      disabled={isCalculating}
                      className="bcs-select"
                    >
                      <option value="1">1 - Emaciated</option>
                      <option value="2">2 - Very Thin</option>
                      <option value="3">3 - Thin</option>
                      <option value="4">4 - Moderately Thin</option>
                      <option value="5">5 - Ideal ✓</option>
                      <option value="6">6 - Moderately Above Ideal</option>
                      <option value="7">7 - Overweight</option>
                      <option value="8">8 - Obesity</option>
                      <option value="9">9 - Severe Obesity</option>
                    </select>
                    <span className="bcs-description">Current: {getBCSDescription(bcs)}</span>
                  </div>
                </div>

                {/* Right Column: Breed Selection */}
                <div className="form-right-column">
                  <div className="form-group breed-selection-group">
                    <label htmlFor="breed">Breed: *</label>
                    <div className="breed-selector" ref={breedDropdownRef}>
                      {breed ? (
                        // Show selected breed with option to change
                        <div className="selected-breed">
                          <span className="selected-breed-name">{breed}</span>
                          <button 
                            type="button" 
                            className="change-breed-btn"
                            onClick={handleBackToCategories}
                            disabled={isCalculating}
                          >
                            Change Breed
                          </button>
                        </div>
                      ) : showCategories ? (
                        // Show global search and breed categories
                        <div className="breed-categories">
                          {/* Global Search Bar */}
                          <div className="global-search-container" ref={globalSearchRef}>
                            <div className="global-search-wrapper">
                              <i className="fas fa-search global-search-icon"></i>
                              <input
                                type="text"
                                value={globalSearchTerm}
                                onChange={(e) => {
                                  setGlobalSearchTerm(e.target.value);
                                  setShowGlobalSearchResults(e.target.value.length >= 2);
                                }}
                                onFocus={() => {
                                  if (globalSearchTerm.length >= 2) {
                                    setShowGlobalSearchResults(true);
                                  }
                                }}
                                placeholder="Search all breeds..."
                                disabled={isCalculating}
                                className="global-search-input"
                                autoComplete="off"
                              />
                              {globalSearchTerm && (
                                <button
                                  type="button"
                                  className="global-search-clear"
                                  onClick={() => {
                                    setGlobalSearchTerm('');
                                    setShowGlobalSearchResults(false);
                                  }}
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              )}
                            </div>
                            {showGlobalSearchResults && globalSearchResults.length > 0 && (
                              <div className="global-search-dropdown">
                                {globalSearchResults.map((breedData) => (
                                  <div
                                    key={breedData.name}
                                    className="global-search-option"
                                    onClick={() => handleGlobalSearchSelect(breedData)}
                                  >
                                    <span className="breed-name">{breedData.name}</span>
                                    <span className="breed-size">{breedData.sizeCategory}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {showGlobalSearchResults && globalSearchTerm.length >= 2 && globalSearchResults.length === 0 && (
                              <div className="global-search-dropdown">
                                <div className="global-search-no-results">
                                  No breeds found matching "{globalSearchTerm}"
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="breed-divider">
                            <span className="divider-text">or browse by category</span>
                          </div>

                          <div className="categories-grid">
                            {breedCategories.map((category) => (
                              <div
                                key={category.name}
                                className="category-card"
                                onClick={() => handleCategorySelect(category)}
                              >
                                <div className="category-name">{category.name}</div>
                                <div className="category-description">{category.description}</div>
                                <div className="category-count">{category.breeds.length} breeds</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : selectedCategory ? (
                        // Show breeds within selected category
                        <div className="category-breeds">
                          <div className="category-header">
                            <button 
                              type="button" 
                              className="back-to-categories-btn"
                              onClick={handleBackToCategories}
                            >
                              ← Back to Categories
                            </button>
                            <span className="category-title">{selectedCategory.name}</span>
                          </div>
                          <input
                            type="text"
                            value={breedSearchTerm}
                            onChange={(e) => setBreedSearchTerm(e.target.value)}
                            placeholder={`Search ${selectedCategory.name.toLowerCase()}...`}
                            disabled={isCalculating}
                            className="breed-search-input"
                            autoComplete="off"
                          />
                          <div className="breed-list">
                            {filteredBreeds.map((breedData) => (
                              <div
                                key={breedData.name}
                                className="breed-option"
                                onClick={() => handleBreedSelect(breedData)}
                              >
                                <span className="breed-name">{breedData.name}</span>
                                <span className="breed-size">{breedData.sizeCategory}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              <button 
                className="calculate-button"
                onClick={calculateCalories}
                  disabled={isCalculating || !weight || !breed}
              >
                {isCalculating ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Calculating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-calculator"></i>
                    Calculate ▶
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="result-section">
              <div className="profile-summary">
                <h3>Dog Profile:</h3>
                <p>
                    <strong>{result.profile.breed}</strong> • {result.profile.weight} {result.profile.weightUnit} • {getAgeLabel(result.profile.age, result.profile.breedSizeCategory)} • {result.profile.activity.charAt(0).toUpperCase() + result.profile.activity.slice(1)} Activity • {result.profile.neuteredStatus.charAt(0).toUpperCase() + result.profile.neuteredStatus.slice(1)} • BCS {result.profile.bodyCondition}
                </p>
              </div>

              <div className="calorie-display">
                <div className="calorie-badge">
                  <span className="calorie-icon">🔥</span>
                  <div className="calorie-info">
                    <span className="calorie-range">{result.calorieRange.target.toLocaleString()}</span>
                    <span className="calorie-unit">kcal/day</span>
                  </div>
                </div>
              </div>

              <div className="meal-frequency-section">
                <h3>Recommended meal frequency:</h3>
                <p className="meal-frequency-text">{result.mealFrequency.description}</p>
              </div>

              <div className="meal-plans-section">
                <h3>Example meal options:</h3>
                <div className="meal-plans-tabs">
                  {result.mealPlans.map((plan, planIndex) => (
                    <div key={planIndex} className="meal-plan-option">
                      <h4 className="plan-title">{planIndex + 1}. {plan.title}</h4>
                      <div className="meal-plan-list">
                        {plan.items.map((meal, index) => (
                          <div key={index} className="meal-item">
                            <div className="meal-info">
                              <span className="meal-amount">{meal.amount}</span>
                              <span className="meal-food">{meal.food}</span>
                            </div>
                            <span className="meal-calories">({meal.calories} kcal)</span>
                          </div>
                        ))}
                      </div>
                      <div className="plan-total">
                        <strong>Total: {plan.totalCalories} kcal</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="special-notes-section">
                <h3>Special Notes:</h3>
                <ul className="notes-list">
                  {result.specialNotes.map((note, index) => (
                    <li key={index}>
                      <span className="note-icon">•</span>
                      <span>{note}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="disclaimer">
                <p><strong>Professional Disclaimer:</strong></p>
                  <p><em>Calculations based on Merck Veterinary Manual RER formulas and WSAVA Global Nutrition Guidelines. Modified Atwater factors used for portion calculations. Vegan formulations require professional validation against complete nutrient profiles. Always consult a veterinary nutritionist for diet formulation, especially for puppies or dogs with medical conditions.</em></p>
                <p><em><strong>Sources:</strong> FEDIAF Nutritional Guidelines, Merck Veterinary Manual, WSAVA Global Nutrition Guidelines.</em></p>
              </div>

              <button 
                className="new-calculation-button"
                onClick={() => {
                  setResult(null);
                  setWeight('');
                  setBreed('');
                  setBreedSearchTerm('');
                  setSelectedBreedData(null);
                  setGlobalSearchTerm('');
                  setShowGlobalSearchResults(false);
                  setSelectedCategory(null);
                  setShowCategories(true);
                  setBcs(5);
                }}
              >
                <i className="fas fa-plus"></i>
                New Calculation
              </button>
            </div>
          )}
        </div>
      </div>
    </div>

      {showBCSChart && <BCSChartModal onClose={() => setShowBCSChart(false)} />}
    </>
  );
};

export default FeedingCalculator;
